'use client';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { DayPicker, type ClassNames } from 'react-day-picker';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

type ExtendedCalendarProps = {
  vertical?: boolean;
  fixedWeeks?: boolean;
  todayIndicator?: boolean;
  /**
   * Days rendered as outlined + dimmed — e.g. days that already have a
   * delivery and can't be picked again. The border sits on the day button
   * (not the `<td>` cell) so it lines up with the button on every layout.
   */
  outlinedDays?: Date[];
};

function CalendarNew({
  className,
  classNames,
  showOutsideDays = false,
  vertical = false,
  fixedWeeks = false,
  showWeekNumber = false,
  disableNavigation = false,
  hideNavigation = false,
  animate = false,
  numberOfMonths = 1,
  todayIndicator = true,
  outlinedDays,
  modifiers,
  modifiersClassNames,
  ...props
}: CalendarProps & ExtendedCalendarProps) {
  const size = 'w-full h-12 md:h-12';
  const selectedDays = 'selected' in props ? props.selected : undefined;
  const navButtonClass = cn(
    buttonVariants({ variant: 'ghost', size: 'adjusted' }),
    'bg-transparent p-0 absolute top-0 h-8 w-12 aria-disabled:opacity-30 [&_svg]:size-6 [&_svg]:stroke-1 [&_svg]:stroke-white [&_svg]:fill-primary opacity-100 hover:opacity-100',
  );
  // Dimming stays on the cell (same level as `disabled`, so opacity doesn't
  // compound) while the border/text sit on the button so it lines up exactly.
  const outlinedDayClass = cn(
    'opacity-50',
    '[&_button]:border [&_button]:border-brand-green2 [&_button]:bg-transparent [&_button]:text-brand-green2 [&_button]:cursor-not-allowed',
  );

  return (
    <DayPicker
      disableNavigation={disableNavigation}
      hideNavigation={hideNavigation}
      animate={animate}
      numberOfMonths={numberOfMonths}
      showWeekNumber={showWeekNumber}
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn('relative p-0', className)}
      modifiers={{
        currentSelected: selectedDays || [],
        outlined: outlinedDays ?? [],
        ...modifiers,
      }}
      modifiersClassNames={{ outlined: outlinedDayClass, ...modifiersClassNames }}
      classNames={{
        months: cn('flex', {
          'flex-col gap-y-4': vertical,
          'flex-row items-start gap-10 sm:gap-y-0': !vertical,
        }),
        month: 'space-y-4 w-full flex flex-col justify-center',
        month_caption: 'flex justify-center pt-1 relative items-center pb-4 border-b mb-2',
        caption_label: 'text-base font-medium capitalize',
        nav: 'absolute top-0 left-0 right-0 z-10',
        button_previous: cn('left-0', navButtonClass),
        button_next: cn('right-0', navButtonClass, ),
        month_grid: 'w-full border-collapse',
        weekdays: 'grid place-items-center grid-cols-7 w-full mb-2',
        weekday:
          'text-muted-foreground rounded-lg w-9 sm:w-10 uppercase font-medium text-xs',
        week: 'w-full grid grid-cols-7 place-items-center py-1 gap-1',
        day: cn(
          'text-center',
          size,
        ),
        range_start:
          "relative after:absolute after:top-0 after:right-0 after:-z-10 after:h-full after:w-1/2 after:bg-accent after:content-['']",
        range_end:
          "relative after:absolute after:top-0 after:left-0 after:-z-10 after:h-full after:w-1/2 after:bg-accent after:content-['']",
        range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground [&_button]:bg-transparent [&_button]:text-primary! [&_button]:hover:bg-primary/10',
        today: todayIndicator ? "relative after:absolute after:bottom-2 after:left-1/2 after:-translate-x-1/2 after:size-1.5 after:rounded-full after:bg-orange-500 after:content-['']" : '',
        day_button: cn(
          buttonVariants({ variant: 'ghost', size: 'adjusted' }),
          'p-0 font-medium aria-selected:opacity-100 bg-white text-primary relative z-0 max-w-12 text-base',
          size,
        ),
        selected:
          '[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground [&_button]:focus:bg-primary [&_button]:focus:text-primary-foreground',
        outside:
          'day-outside text-brand-green2! opacity-50 aria-selected:bg-brand-primary aria-selected:text-muted-foreground aria-selected:opacity-30',
        disabled: 'bg-transparent text-brand-green2! opacity-50!',
        hidden: 'invisible',
        weeks: '',
        ...classNames,
        
      }}
      {...props}
    />
  );
}
CalendarNew.displayName = 'CalendarNew';

export { CalendarNew };
export type { ClassNames as CalendarClassNames };
